import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslate } from 'react-admin';
import { useDebounce } from 'src/hooks/use-debounce';
import { PredictionMarketItem } from '../types';
import EventsTab from './EventsTab';
import MarketsTab from './MarketsTab';
import { SearchMarketDialogProvider } from './SearchMarketDialogContext';

type SearchTabValue = 'markets' | 'events';

function CustomTabPanel({
  children,
  value,
  tabValue,
}: {
  children: any;
  value: SearchTabValue;
  tabValue: SearchTabValue;
}) {
  return (
    <div role="tabpanel" hidden={value !== tabValue}>
      {value === tabValue ? <Box sx={{ pt: 2 }}>{children}</Box> : null}
    </div>
  );
}

type SearchMarketDialogProps = {
  open: boolean;
  selectedMarketIds: Array<string | number>;
  onClose: () => void;
  onSelectMarket: (market: PredictionMarketItem) => void;
};

export default function SearchMarketDialog({
  open,
  selectedMarketIds,
  onClose,
  onSelectMarket,
}: SearchMarketDialogProps) {
  const translate = useTranslate();
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 300);
  const [searchTab, setSearchTab] = useState<SearchTabValue>('events');

  useEffect(() => {
    if (open) {
      setSearchTab('events');
      setSearchValue('');
    }
  }, [open]);

  const contextValue = useMemo(
    () => ({
      state: {
        open,
        searchValue: debouncedSearchValue,
        selectedIds: new Set(selectedMarketIds.map((id) => `${id}`)),
      },
      actions: {
        setSearchValue,
        selectMarket: onSelectMarket,
      },
      meta: {
        selectedCount: selectedMarketIds.length,
      },
    }),
    [debouncedSearchValue, onSelectMarket, open, selectedMarketIds]
  );

  return (
    <SearchMarketDialogProvider value={contextValue}>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>{translate('module.featuredMarkets.addMarketDialogTitle')}</DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              fullWidth
              size="small"
              label={translate('module.featuredMarkets.searchPlaceholder')}
              placeholder={translate('module.featuredMarkets.searchHelp')}
              value={searchValue}
              onChange={(event) => contextValue.actions.setSearchValue(event.target.value)}
            />

            <Tabs
              value={searchTab}
              onChange={(_, value) => setSearchTab(value)}
              variant="fullWidth"
            >
              <Tab label={translate('module.featuredMarkets.eventsTab')} value="events" />
              <Tab label={translate('module.featuredMarkets.marketsTab')} value="markets" />
            </Tabs>

            <CustomTabPanel value={searchTab} tabValue="events">
              <EventsTab active={searchTab === 'events'} />
            </CustomTabPanel>

            <CustomTabPanel value={searchTab} tabValue="markets">
              <MarketsTab active={searchTab === 'markets'} />
            </CustomTabPanel>
          </Stack>
        </DialogContent>
      </Dialog>
    </SearchMarketDialogProvider>
  );
}
